import { trackScreenViewed } from '@/lib/analytics/scribe/ui';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type AuthenticationProvider } from '@/models/analytics/features/onboarding';
import {
  type DictateOnboardingStepCompletedEventProps,
  type DictateOnboardingStepSkippedEventProps,
  type DictationOnboardingAnalyticsPorts,
} from '@heidi/dictate/contracts';
import { type ScreenName } from '@heidi/runtime/analytics-vocabulary';

/**
 * App-side emit hosts for the desktop dictate onboarding funnel
 * (`@heidi/dictate`). The hook that builds the payloads moved into the
 * package and reaches these through the analytics seam.
 *
 * ROUTING: NO `options` on any of the four funnel events, exactly as the
 * dispatches these replace carried — the funnel's Amplitude series are live.
 * `screen_viewed` binds to the app wrapper, which passes none either.
 *
 * The enum members, not string literals: `event_name` discriminates
 * `TrackingEventV2` on string ENUM members. The moved hook's contracts declare
 * the same wire values; the seam's suite asserts they agree.
 */

export const trackDictateOnboardingStarted: DictationOnboardingAnalyticsPorts['trackOnboardingStarted'] =
  (props) => {
    track({
      event_name: TrackingEventName.OnboardingStarted,
      props,
    });
  };

export const trackDictateOnboardingStepCompleted: DictationOnboardingAnalyticsPorts['trackStepCompleted'] =
  (props: DictateOnboardingStepCompletedEventProps) => {
    track({
      event_name: TrackingEventName.OnboardingStepCompleted,
      // `auth_method` is the one field whose app type is a string ENUM the
      // inert door cannot name; the wire strings are identical and the host's
      // suite asserts the emitted value, which is what makes this cast a
      // formality rather than a claim.
      props: {
        ...props,
        auth_method: props.auth_method as AuthenticationProvider | undefined,
      },
    });
  };

export const trackDictateOnboardingStepSkipped: DictationOnboardingAnalyticsPorts['trackStepSkipped'] =
  (props: DictateOnboardingStepSkippedEventProps) => {
    track({
      event_name: TrackingEventName.OnboardingStepSkipped,
      props,
    });
  };

export const trackDictateOnboardingCompleted: DictationOnboardingAnalyticsPorts['trackOnboardingCompleted'] =
  (props) => {
    track({
      event_name: TrackingEventName.OnboardingCompleted,
      props,
    });
  };

/** One bundle for the bootstrap wiring. A module constant, so it is the same
 * object every time and cannot re-fire an effect that depends on it. */
/**
 * The one adapted member: the app wrapper's param types the screen name as the
 * `ScreenName` enum the inert door cannot name. The hook passes the enum's own
 * value, so the cast re-tags rather than converts; the host's suite asserts the
 * emitted screen name.
 */
const trackScreenViewedHost: DictationOnboardingAnalyticsPorts['trackScreenViewed'] = ({
  screen_name,
}) => {
  trackScreenViewed({ screen_name: screen_name as ScreenName });
};

export const dictateOnboardingAnalyticsPorts: DictationOnboardingAnalyticsPorts = {
  trackOnboardingStarted: trackDictateOnboardingStarted,
  trackStepCompleted: trackDictateOnboardingStepCompleted,
  trackStepSkipped: trackDictateOnboardingStepSkipped,
  trackOnboardingCompleted: trackDictateOnboardingCompleted,
  trackScreenViewed: trackScreenViewedHost,
};
